import * as React from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';

import { getMessageByKey } from 'Stores/LanguageStore';
import { CancellationReasonOptionDto } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { TextArea } from 'Views/Components/New/TextArea';
import { RefundModel } from './NewCancelBookingModal';

interface CancellationReasonPickerProps {
	options: CancellationReasonOptionDto[];
	required: boolean;
	refundModel: RefundModel;
	/** Set once the user has tried to confirm, so the field errors aren't shown before they act. */
	showErrors: boolean;
	/** Lets the wizard scroll this into view when confirming is blocked by a missing reason. */
	containerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Resolves an admin-authored label through the language table, falling back to the plain text.
 *
 * Both halves are stored and both are sent, so a key that hasn't been translated (or was never
 * seeded) still renders the English rather than an empty label. Same contract the dynamic booking
 * fields use.
 */
const resolveText = (text?: string, key?: string): string => {
	if (key) {
		return getMessageByKey(key, text) ?? text ?? '';
	}
	return text ?? '';
};

/** The reason the user has selected, or undefined. */
export const selectedReason = (
	options: CancellationReasonOptionDto[],
	refundModel: RefundModel,
): CancellationReasonOptionDto | undefined =>
	options.find(option => option.id === refundModel.cancellationReasonId);

/**
 * Why the reason itself is not acceptable, if it isn't. Separate from the detail error below so each
 * message lands on the control it is about — a "please provide more information" shown against the
 * reason dropdown points at the wrong field.
 */
const reasonSelectionError = (
	options: CancellationReasonOptionDto[],
	required: boolean,
	refundModel: RefundModel,
): string => {
	// Nothing to pick — the tenant has no reasons that apply to this booking and this actor. Never
	// block the cancellation on a configuration gap.
	if (options.length === 0) return '';
	if (selectedReason(options, refundModel)) return '';
	return required ? 'Please choose a reason for the cancellation.' : '';
};

/** Why the more-information text is not acceptable, if it isn't. */
const detailError = (options: CancellationReasonOptionDto[], refundModel: RefundModel): string => {
	const reason = selectedReason(options, refundModel);
	if (!reason?.showMoreInformation || !reason.moreInformationRequired) return '';
	if (refundModel.cancellationReasonDetail?.trim()) return '';

	const label = resolveText(reason.moreInformationLabel, reason.moreInformationLabelKey);
	return label ? `Please provide ${label.toLowerCase()}.` : 'Please provide more information.';
};

/**
 * Whether the wizard may proceed. Kept next to the picker rather than in the wizard so the rule that
 * decides the error message and the rule that blocks Confirm cannot drift apart.
 */
export const cancellationReasonError = (
	options: CancellationReasonOptionDto[],
	required: boolean,
	refundModel: RefundModel,
): string =>
	reasonSelectionError(options, required, refundModel) || detailError(options, refundModel);

/**
 * The reason picker, rendered on the wizard's confirmation step.
 *
 * It lives on `confirm` rather than a step of its own because `confirm` is the only step the wizard
 * always builds — every other one drops out depending on the refund shape, the actor's role or the
 * product. It also avoids the step list changing shape once the reasons fetch resolves, which would
 * move the user mid-flow (the step counter is clamped to a list recomputed on every render).
 */
export const CancellationReasonPicker = observer(
	({ options, required, refundModel, showErrors, containerRef }: CancellationReasonPickerProps) => {
		if (options.length === 0) {
			return null;
		}

		const reason = selectedReason(options, refundModel);
		const selectionError = showErrors ? reasonSelectionError(options, required, refundModel) : '';
		const moreInfoError = showErrors ? detailError(options, refundModel) : '';

		// Choosing, changing or clearing the reason. The dropdown is clearable, so a reason picked by
		// mistake can be undone — including when one is required, where blocking the confirm is the
		// right way to say "you still need to choose" rather than trapping the first thing clicked.
		const select = (id?: string) => {
			const next = refundModel.cancellationReasonId === id ? undefined : id;
			runInAction(() => {
				refundModel.cancellationReasonId = next;
				// The detail belongs to the reason that asked for it, so changing or clearing the reason
				// drops it rather than carrying an answer across to a different question.
				refundModel.cancellationReasonDetail = undefined;
			});
		};

		const setDetail = (value: string) => {
			runInAction(() => {
				refundModel.cancellationReasonDetail = value;
			});
		};

		return (
			<div ref={containerRef} className="mt-6 pt-5 border-t border-slate-200 space-y-3">
				<SelectInput
					label="Reason for cancelling"
					required={required}
					isClearable
					// Forced upwards, not "auto" (which SelectInput already defaults to and which did not
					// work here): auto measures the space below against the control's scroll parent, and
					// inside a scrollable modal that parent can still scroll, so it concludes there is
					// room and opens downward off the bottom of the screen. This control is always the
					// last thing on the confirmation step, so upward is simply correct.
					menuPlacement="top"
					options={options.map(option => ({
						value: option.id,
						label: resolveText(option.reason, option.reasonKey),
					}))}
					value={
						reason ? { value: reason.id, label: resolveText(reason.reason, reason.reasonKey) } : null
					}
					onChange={(selected: any) => select(selected?.value)}
					error={selectionError || undefined}
					data-test-id="cancellation-reason-select"
				/>

				{reason?.showMoreInformation && (
					<TextArea
						label={
							resolveText(reason.moreInformationLabel, reason.moreInformationLabelKey)
							|| 'More information'
						}
						required={reason.moreInformationRequired}
						rows={3}
						maxLength={500}
						placeholder={resolveText(
							reason.moreInformationPlaceholder,
							reason.moreInformationPlaceholderKey,
						)}
						value={refundModel.cancellationReasonDetail ?? ''}
						onChange={event => setDetail(event.target.value)}
						error={moreInfoError || undefined}
						data-test-id="cancellation-reason-detail"
					/>
				)}
			</div>
		);
	},
);
